import React, { useState, useMemo, useEffect } from "react";
import galleryImages from "../data/gallery";
import customGallery from "../data/customGallery";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import { Search } from "lucide-react";

// Fade-in on scroll
function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fadeUp");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".scroll-fade");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

function Gallery() {
  const [index, setIndex] = useState(-1);
  const [activeList, setActiveList] = useState([]);
  const [visibleStoreCount, setVisibleStoreCount] = useState(12); // how many store images to show at first

  useScrollAnimation();

  // Randomize store gallery, keep custom gallery fixed
  const shuffledStore = useMemo(
    () => [...galleryImages].sort(() => Math.random() - 0.5),
    []
  );
  const fixedCustom = customGallery;

  const openLightbox = (list, i) => {
    setActiveList(list);
    setIndex(i);
  };

  const slides =
    activeList === "custom"
      ? fixedCustom.map((img) => ({ src: img.src, title: img.alt }))
      : shuffledStore.map((img) => ({ src: img.src, title: img.alt }));

  const visibleStoreImages = shuffledStore.slice(0, visibleStoreCount);
  const hasMoreStoreImages = visibleStoreCount < shuffledStore.length;

  return (
    <main className="pt-24 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* === Custom Creations === */}
        <section className="scroll-fade mb-24">
          <h2 className="text-4xl font-bold text-brand-red mb-4 text-center">
            Custom Creations
          </h2>
          <p className="text-center text-gray-700 mb-12 text-lg max-w-3xl mx-auto">
            Our custom creations are built around your vision — from choosing
            your stain and leather to designing one-of-a-kind furniture that
            fits your home perfectly.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {fixedCustom.map((img, i) => (
              <div
                key={`custom-${i}`}
                className="relative group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white"
                onClick={() => openLightbox("custom", i)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-80 object-contain transition-transform duration-500 ease-in-out group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center">
                  <Search className="text-white w-8 h-8 mb-2 animate-fadeIn" />
                  <span className="text-white text-lg font-semibold tracking-wide">
                    Click to View
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="border-t-4 border-brand-red w-32 mx-auto mb-24 rounded-full opacity-70 scroll-fade"></div>

        {/* === Store Gallery === */}
        <section className="scroll-fade mb-24">
          <h2 className="text-4xl font-bold text-brand-red mb-4 text-center">
            Store Gallery
          </h2>
          <p className="text-center text-gray-700 mb-12 text-lg max-w-3xl mx-auto">
            A look inside our store and featured furniture pieces — giving you a
            glimpse of the Texana Trading Co. experience.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {visibleStoreImages.map((img, i) => (
              <div
                key={`store-${i}`}
                className="relative group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white"
                onClick={() => openLightbox("store", i)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-80 object-contain transition-transform duration-500 ease-in-out group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center">
                  <Search className="text-white w-8 h-8 mb-2 animate-fadeIn" />
                  <span className="text-white text-lg font-semibold tracking-wide">
                    Click to View
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Load more button */}
          {hasMoreStoreImages && (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() =>
                  setVisibleStoreCount((prev) => prev + 9) // load 9 more per click
                }
                className="px-6 py-3 rounded-full border border-brand-red text-brand-red font-semibold hover:bg-brand-red hover:text-white transition-colors duration-300 shadow-sm"
              >
                Load More Photos
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Lightbox Viewer */}
      <Lightbox
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        slides={slides}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 5,
          zoomInMultiplier: 1.8,
          doubleTapDelay: 250,
          doubleClickDelay: 250,
          keyboardMoveDistance: 40,
        }}
      />
    </main>
  );
}

export default Gallery;